/** ISO 3166-1 alpha-2 → ITU-T E.164 country calling code for phone entry UI. */

export type PhoneCountry = {
  iso: string;
  name: string;
  callingCode: string;
};

/**
 * Curated list for WhatsApp-style country pickers.
 * Nigeria and Ghana lead; the rest are alphabetical for scanability.
 */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  { iso: 'NG', name: 'Nigeria', callingCode: '234' },
  { iso: 'GH', name: 'Ghana', callingCode: '233' },
  { iso: 'BJ', name: 'Benin', callingCode: '229' },
  { iso: 'BF', name: 'Burkina Faso', callingCode: '226' },
  { iso: 'CM', name: 'Cameroon', callingCode: '237' },
  { iso: 'CA', name: 'Canada', callingCode: '1' },
  { iso: 'CI', name: "Côte d'Ivoire", callingCode: '225' },
  { iso: 'EG', name: 'Egypt', callingCode: '20' },
  { iso: 'ET', name: 'Ethiopia', callingCode: '251' },
  { iso: 'FR', name: 'France', callingCode: '33' },
  { iso: 'DE', name: 'Germany', callingCode: '49' },
  { iso: 'IN', name: 'India', callingCode: '91' },
  { iso: 'IE', name: 'Ireland', callingCode: '353' },
  { iso: 'IT', name: 'Italy', callingCode: '39' },
  { iso: 'KE', name: 'Kenya', callingCode: '254' },
  { iso: 'LR', name: 'Liberia', callingCode: '231' },
  { iso: 'ML', name: 'Mali', callingCode: '223' },
  { iso: 'NL', name: 'Netherlands', callingCode: '31' },
  { iso: 'NE', name: 'Niger', callingCode: '227' },
  { iso: 'RW', name: 'Rwanda', callingCode: '250' },
  { iso: 'SN', name: 'Senegal', callingCode: '221' },
  { iso: 'SL', name: 'Sierra Leone', callingCode: '232' },
  { iso: 'ZA', name: 'South Africa', callingCode: '27' },
  { iso: 'ES', name: 'Spain', callingCode: '34' },
  { iso: 'TG', name: 'Togo', callingCode: '228' },
  { iso: 'UG', name: 'Uganda', callingCode: '256' },
  { iso: 'AE', name: 'United Arab Emirates', callingCode: '971' },
  { iso: 'GB', name: 'United Kingdom', callingCode: '44' },
  { iso: 'US', name: 'United States', callingCode: '1' },
  { iso: 'TZ', name: 'Tanzania', callingCode: '255' },
  { iso: 'ZM', name: 'Zambia', callingCode: '260' },
  { iso: 'ZW', name: 'Zimbabwe', callingCode: '263' },
] as const;

export const DEFAULT_PHONE_COUNTRY: PhoneCountry = PHONE_COUNTRIES[0]!;

const BY_ISO = new Map(PHONE_COUNTRIES.map((row) => [row.iso, row]));

/** Regional-indicator flag emoji from an ISO country code (e.g. NG → 🇳🇬). */
export function phoneCountryFlag(iso: string): string {
  const code = iso.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return [...code].map((char) => String.fromCodePoint(127397 + char.charCodeAt(0))).join('');
}

export function phoneCountryByIso(iso: string | undefined): PhoneCountry {
  if (!iso) return DEFAULT_PHONE_COUNTRY;
  return BY_ISO.get(iso.toUpperCase()) ?? DEFAULT_PHONE_COUNTRY;
}

/** Longest calling-code match first so +234 wins over +2, etc. Prefer US for shared +1. */
const BY_CALLING_CODE_LENGTH = [...PHONE_COUNTRIES].sort((a, b) => {
  const byLength = b.callingCode.length - a.callingCode.length;
  if (byLength !== 0) return byLength;
  if (a.iso === 'US') return -1;
  if (b.iso === 'US') return 1;
  return a.name.localeCompare(b.name);
});

export type ParsedPhoneParts = {
  country: PhoneCountry;
  national: string;
};

/**
 * Split an E.164 (or near-E.164) value into country + national digits.
 * Falls back to Nigeria when the calling code is unknown.
 */
export function parsePhoneParts(value: string, fallbackIso = 'NG'): ParsedPhoneParts {
  const trimmed = value.trim();
  const digits = trimmed.replace(/[^\d]/g, '');
  if (!digits) {
    return { country: phoneCountryByIso(fallbackIso), national: '' };
  }

  for (const country of BY_CALLING_CODE_LENGTH) {
    if (digits.startsWith(country.callingCode) && digits.length > country.callingCode.length) {
      return {
        country,
        national: digits.slice(country.callingCode.length).replace(/^0+/, ''),
      };
    }
  }

  // Local NG-style 0… without a country selected yet
  if (!trimmed.startsWith('+') && digits.startsWith('0')) {
    const country = phoneCountryByIso(fallbackIso);
    return { country, national: digits.replace(/^0+/, '') };
  }

  return { country: phoneCountryByIso(fallbackIso), national: digits.replace(/^0+/, '') };
}

/** Build E.164 from calling code + national number. Strips a leading 0 from national. */
export function composePhoneE164(callingCode: string, national: string): string {
  const nationalDigits = national.replace(/[^\d]/g, '').replace(/^0+/, '');
  if (!nationalDigits) return '';
  const code = callingCode.replace(/[^\d]/g, '');
  if (!code) return '';
  return `+${code}${nationalDigits}`;
}
